import {
  Dialog,
  DialogPanel,
  DialogTitle,
  DialogBackdrop,
} from '@headlessui/react';
import { XMarkIcon, ExclamationTriangleIcon } from '@heroicons/react/24/solid';
import { useReactFlow } from '@xyflow/react';
import { pick, isEqual } from 'lodash';
import { useMemo, useState, useEffect } from 'react';
import Alert from 'src/components/alert';
import ButtonAction from 'src/components/button-action';
import { useGlobalContext } from 'src/context/global/use-global-context';
import { getEdgeName } from 'src/utils/node';
import { twMerge } from 'tailwind-merge';

import { EEdgeEditTab, IEdgeExtraData } from '../types';
import { CustomEdgeEditCableType } from './edit-cable-type';
import { CustomEdgeEditDescription } from './edit-description';
import { CustomEdgeEditLink } from './edit-link';
import { CustomEdgeEditMonitoring } from './edit-monitoring';
import { CustomEdgeEditStyle } from './edit-style';
import { CustomEdgeEditVlan } from './edit-vlan';

interface DialogCustomEdgeEditProps {
  ids: string[];
  onCancel: () => void;
  data: IEdgeExtraData;
  onChangeEdgeData: (data: Partial<IEdgeExtraData>) => void;
  isMultiple?: boolean;
  onSave?: (data: Partial<IEdgeExtraData>, closeDialog?: boolean) => void;
}

const TAB_FIELDS: Record<EEdgeEditTab, (keyof IEdgeExtraData)[]> = {
  [EEdgeEditTab.STYLE]: ['edgeType'],
  [EEdgeEditTab.DESCRIPTION]: ['useCustomDescription', 'customDescription'],
  [EEdgeEditTab.LINK]: [
    'enableLink',
    'autoNegotiation',
    'linkSpeed',
    'linkDuplex',
    'linkMdi',
  ],
  [EEdgeEditTab.VLAN]: ['vlanIds', 'untaggedVlan', 'allowIncomingPackets'],
  [EEdgeEditTab.MONITORING]: ['enableMonitoring'],
  [EEdgeEditTab.CABLE_TYPE]: ['cableType'],
};

export function DialogCustomEdgeEdit({
  ids,
  onCancel,
  data,
  onChangeEdgeData,
  isMultiple = false,
  onSave,
}: DialogCustomEdgeEditProps) {
  const { getNodes } = useReactFlow();
  const { vlans } = useGlobalContext();
  const hasVlanIds = useMemo(() => vlans?.length > 0, [vlans]);

  const tabs = useMemo(
    () => [
      { key: EEdgeEditTab.STYLE, name: 'Style' },
      { key: EEdgeEditTab.DESCRIPTION, name: 'Description' },
      { key: EEdgeEditTab.LINK, name: 'Link' },
      { key: EEdgeEditTab.VLAN, name: 'VLAN', disabled: !hasVlanIds },
      { key: EEdgeEditTab.MONITORING, name: 'Monitoring' },
      { key: EEdgeEditTab.CABLE_TYPE, name: 'Cable Type' },
    ],
    [hasVlanIds],
  );

  const [currentTab, setCurrentTab] = useState(tabs[0].key);
  const [savedData, setSavedData] = useState<IEdgeExtraData>(data);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [pendingTab, setPendingTab] = useState<EEdgeEditTab | null>(null);
  const [isClosing, setIsClosing] = useState(false);

  // Sync savedData with data if it was changed externally but not by our local edits
  // Actually, we want to keep savedData as the reference for what was "saved" to edges
  useEffect(() => {
    setSavedData(data);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const edgeNames = useMemo(
    () => ids.map((id) => ({ id, name: getEdgeName(id, getNodes()) })),
    [ids, getNodes],
  );

  const isTabDirty = (tab: EEdgeEditTab) => {
    const fields = TAB_FIELDS[tab];
    const currentTabData = pick(data, fields);
    const savedTabData = pick(savedData, fields);
    return !isEqual(currentTabData, savedTabData);
  };

  const handleTabChange = (tabKey: EEdgeEditTab) => {
    if (tabKey === currentTab) return;

    if (isTabDirty(currentTab)) {
      setPendingTab(tabKey);
      setShowConfirmation(true);
    } else {
      setCurrentTab(tabKey);
    }
  };

  const handleSave = (closeDialog = true) => {
    const fields = TAB_FIELDS[currentTab];
    const dataToSave = pick(data, fields);
    onSave?.(dataToSave, closeDialog);
    setSavedData((prev) => ({ ...prev, ...dataToSave }));
  };

  const handleSaveClick = () => {
    handleSave(true);
  };

  const handleCloseAttempt = () => {
    if (isTabDirty(currentTab)) {
      setIsClosing(true);
      setShowConfirmation(true);
    } else {
      onCancel();
    }
  };

  const handleConfirmApply = () => {
    handleSave(false);
    setShowConfirmation(false);
    if (isClosing) {
      onCancel();
    } else if (pendingTab) {
      setCurrentTab(pendingTab);
      setPendingTab(null);
    }
    setIsClosing(false);
  };

  const handleConfirmDismiss = () => {
    const fields = TAB_FIELDS[currentTab];
    const dataToRevert = pick(savedData, fields);
    onChangeEdgeData(dataToRevert);
    setShowConfirmation(false);
    if (isClosing) {
      onCancel();
    } else if (pendingTab) {
      setCurrentTab(pendingTab);
      setPendingTab(null);
    }
    setIsClosing(false);
  };

  const handleConfirmCancel = () => {
    setShowConfirmation(false);
    setPendingTab(null);
    setIsClosing(false);
  };

  return (
    <>
      <Dialog
        open
        onClose={handleCloseAttempt}
        className="relative z-50"
        data-testid="dialog-custom-edge-edit"
      >
        <DialogBackdrop
          transition
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
        />

        <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <DialogPanel
              transition
              data-testid="dialog-custom-edge-edit-panel"
              className={twMerge([
                'relative transform overflow-hidden rounded-lg bg-white px-2 pb-2 pt-3 text-left shadow-xl transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in sm:my-8 sm:p-6 data-[closed]:sm:translate-y-0 data-[closed]:sm:scale-95',
                'min-w-[400px] !pt-0',
              ])}
            >
              <div className="absolute right-2 top-2 flex items-center justify-center gap-2">
                <button
                  type="button"
                  onClick={handleCloseAttempt}
                  data-testid="dialog-custom-edge-edit-close-button"
                  className="h-6 w-6 rounded-full p-1 text-gray-500 hover:bg-gray-100"
                >
                  <XMarkIcon />
                </button>
              </div>
              <div className="mt-2.5 flex w-full flex-col items-start gap-0.5 pr-2">
                {edgeNames.map(({ id, name }) => (
                  <span
                    key={id}
                    className="truncate text-sm font-medium text-gray-500"
                  >
                    {name}
                  </span>
                ))}
              </div>

              <div>
                <div className="mt-4 text-left">
                  <div className="relative border-b border-blue-400 pb-0.5">
                    <nav
                      aria-label="Tabs"
                      className="-mb-px flex space-x-4 overflow-x-auto"
                    >
                      {tabs.map((tab) => (
                        <button
                          key={tab.name}
                          type="button"
                          data-testid={`dialog-custom-edge-edit-tab-button-${tab.key}`}
                          aria-current={
                            currentTab === tab.key ? 'page' : undefined
                          }
                          disabled={tab.disabled}
                          className={twMerge([
                            'text-md whitespace-nowrap border-b-2 px-2 py-1 font-medium disabled:opacity-50',
                            currentTab === tab.key
                              ? 'border-blue-400 bg-blue-400 text-white'
                              : 'border-transparent text-gray-500',
                          ])}
                          onClick={() => handleTabChange(tab.key)}
                        >
                          {tab.name}
                        </button>
                      ))}
                    </nav>
                  </div>
                  {currentTab === 'style' && (
                    <CustomEdgeEditStyle
                      data={data}
                      onChangeEdgeData={onChangeEdgeData}
                    />
                  )}
                  {currentTab === 'description' && (
                    <CustomEdgeEditDescription
                      data={data}
                      onChangeEdgeData={onChangeEdgeData}
                    />
                  )}
                  {currentTab === 'link' && (
                    <CustomEdgeEditLink
                      data={data}
                      onChangeEdgeData={onChangeEdgeData}
                    />
                  )}
                  {currentTab === 'vlan' && (
                    <CustomEdgeEditVlan
                      data={data}
                      onChangeEdgeData={onChangeEdgeData}
                    />
                  )}
                  {currentTab === 'monitoring' && (
                    <CustomEdgeEditMonitoring
                      data={data}
                      onChangeEdgeData={onChangeEdgeData}
                    />
                  )}
                  {currentTab === 'cable-type' && (
                    <CustomEdgeEditCableType
                      data={data}
                      onChangeEdgeData={onChangeEdgeData}
                      edgeIds={ids}
                    />
                  )}
                </div>
                {isMultiple && (
                  <div className="mt-5 flex w-full flex-col items-center justify-center gap-3">
                    <div className="flex w-full flex-wrap items-center gap-2">
                      <ButtonAction
                        type="button"
                        color="blue"
                        variant="contained"
                        data-testid="dialog-custom-edge-edit-save-button"
                        onClick={handleSaveClick}
                        fullWidth
                        className="sm:flex-1"
                      >
                        Save
                      </ButtonAction>
                      <ButtonAction
                        type="button"
                        color="info"
                        variant="outlined"
                        onClick={handleCloseAttempt}
                        fullWidth
                        className="sm:flex-1"
                      >
                        Cancel
                      </ButtonAction>
                    </div>
                    <Alert
                      title="It is recommended to recheck the configuration after applying changes to the connections"
                      type="info"
                      size="sm"
                      className="mt-2 w-full"
                    />
                  </div>
                )}
                {!isMultiple && (
                  <div className="mt-5 w-full sm:mt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3">
                    <ButtonAction
                      type="button"
                      color="info"
                      variant="outlined"
                      onClick={handleCloseAttempt}
                      fullWidth
                      className="mt-3 sm:col-start-1 sm:mt-0"
                      data-testid="dialog-custom-edge-edit-cancel-button"
                    >
                      Cancel
                    </ButtonAction>
                    <ButtonAction
                      type="button"
                      color="blue"
                      variant="contained"
                      data-testid="dialog-custom-edge-edit-save-button"
                      onClick={() => handleSave(true)}
                      fullWidth
                      className="sm:col-start-2"
                    >
                      Save
                    </ButtonAction>
                  </div>
                )}
              </div>
            </DialogPanel>
          </div>
        </div>
      </Dialog>

      {/* Unsaved changes confirmation dialog */}
      <Dialog
        open={showConfirmation}
        onClose={handleConfirmCancel}
        className="relative z-[60]"
      >
        <DialogBackdrop
          transition
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
        />
        <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <DialogPanel
              transition
              className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in sm:my-8 sm:w-full sm:max-w-lg sm:p-6 data-[closed]:sm:translate-y-0 data-[closed]:sm:scale-95"
            >
              <div className="sm:flex sm:items-start">
                <div className="mx-auto flex size-12 shrink-0 items-center justify-center rounded-full bg-yellow-100 sm:mx-0 sm:size-10">
                  <ExclamationTriangleIcon
                    className="size-6 text-yellow-600"
                    aria-hidden="true"
                  />
                </div>
                <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                  <DialogTitle
                    as="h3"
                    className="text-base font-semibold text-gray-900"
                  >
                    Unsaved changes
                  </DialogTitle>
                  <div className="mt-2">
                    <p className="text-sm text-gray-500">
                      You have unsaved changes on the current tab. Would you
                      like to apply them or dismiss them?
                    </p>
                  </div>
                </div>
              </div>
              <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse sm:gap-3">
                <ButtonAction
                  type="button"
                  color="blue"
                  variant="contained"
                  onClick={handleConfirmApply}
                  className="w-full sm:w-auto"
                >
                  Apply
                </ButtonAction>
                <ButtonAction
                  type="button"
                  color="danger"
                  variant="contained"
                  onClick={handleConfirmDismiss}
                  className="mt-3 w-full sm:mt-0 sm:w-auto"
                >
                  Dismiss
                </ButtonAction>
                <ButtonAction
                  type="button"
                  color="info"
                  variant="outlined"
                  onClick={handleConfirmCancel}
                  className="mt-3 w-full sm:mt-0 sm:w-auto"
                >
                  Cancel
                </ButtonAction>
              </div>
            </DialogPanel>
          </div>
        </div>
      </Dialog>
    </>
  );
}
